import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatNumber, formatPct, formatSentiment } from '@/lib/format';
import { Activity, MessageSquare, ScanLine, Sparkles, Zap } from 'lucide-react';
import { CHART_COLORS, CHART_INITIAL_DIMENSIONS, PROVIDERS } from '@/constants/providers';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';

export default function OverviewTab({ project }) {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [byProvider, setByProvider] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, tl, bp, cp] = await Promise.all([
        api.get(`/projects/${project.id}/analytics/overview`),
        api.get(`/projects/${project.id}/analytics/timeline`),
        api.get(`/projects/${project.id}/analytics/by-provider`),
        api.get(`/projects/${project.id}/analytics/competitors`),
      ]);
      setOverview(ov.data);
      setTimeline((tl.data.points || []).map((p, idx) => ({ ...p, x: p.date || `#${idx + 1}` })));
      setByProvider(bp.data.providers || []);
      setCompetitors(cp.data.items || []);
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const totalResults = overview?.total_results ?? 0;
  const noData = totalResults === 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testId="overview-kpi-mention-rate" label="Mention rate" value={formatPct(overview?.mention_rate)} hint={`${formatNumber(overview?.brand_mentions)} mentions in ${formatNumber(totalResults)} answers`} icon={Activity} />
        <KpiCard testId="overview-kpi-sentiment" label="Avg sentiment" value={formatSentiment(overview?.avg_sentiment)} hint="Across live providers" icon={Sparkles} />
        <KpiCard testId="overview-kpi-citations" label="Citations found" value={formatNumber(overview?.citations_count)} hint="URLs cited in AI answers" icon={MessageSquare} />
        <KpiCard testId="overview-kpi-scans" label="Scans" value={formatNumber(overview?.scans_count)} hint={`${formatNumber(overview?.prompts_count)} prompts total`} icon={ScanLine} />
        <KpiCard
          testId="overview-kpi-live-coverage"
          label="Live web coverage"
          value={formatPct(overview?.data_freshness?.native_share)}
          hint={`${formatNumber(overview?.data_freshness?.native || 0)} / ${formatNumber(totalResults)} answers grounded via native web_search`}
          icon={Zap}
        />
      </div>

      {overview?.citations_breakdown && overview.citations_breakdown.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="overview-citations-breakdown">
          <span className="text-muted-foreground uppercase tracking-wide text-[10px] font-medium">Citations attribution</span>
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 bg-emerald-500/10">
            Brand · {formatNumber(overview.citations_breakdown.brand)}
          </Badge>
          <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10">
            Competitors · {formatNumber(overview.citations_breakdown.competitor)}
          </Badge>
          <Badge variant="outline" className="text-muted-foreground">
            Third-party · {formatNumber(overview.citations_breakdown.third_party)}
          </Badge>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <ChartCard title="Mention rate over time" subtitle="By scan (successful answers)" className="lg:col-span-8" testId="chart-mention-rate-card">
          {noData || timeline.length === 0 ? (
            <EmptyState title="No scans yet" description="Run your first scan to see a timeline." />
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSIONS}>
                <LineChart data={timeline} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.6} />
                  <XAxis dataKey="x" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                  <Tooltip formatter={(v) => `${Math.round(v * 1000) / 10}%`} labelFormatter={(l) => `Scan: ${l}`} contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                  <Line type="monotone" dataKey="mention_rate" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Share of voice" subtitle="Brand vs competitors" className="lg:col-span-4" testId="chart-sov-card">
          {noData || competitors.length === 0 ? (
            <EmptyState title="No data yet" description="Add competitors and run a scan." />
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSIONS}>
                <PieChart>
                  <Pie data={competitors.filter((c) => c.count > 0)} dataKey="count" nameKey="name" outerRadius={90} innerRadius={55} paddingAngle={2}>
                    {competitors.filter((c) => c.count > 0).map((entry, idx) => (
                      <Cell key={entry.name} fill={entry.is_brand ? CHART_COLORS[0] : CHART_COLORS[(idx + 1) % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatNumber(v)} contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Mention rate by provider" subtitle="Which AI answers you appear in" className="lg:col-span-6" testId="chart-provider-comparison-card">
          {noData || byProvider.length === 0 ? (
            <EmptyState title="No data yet" description="Run a scan to compare providers." />
          ) : (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSIONS}>
                <BarChart data={byProvider.map((p) => ({ name: PROVIDERS[p.provider]?.short || p.provider, mention_rate: p.mention_rate, runs: p.runs }))} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.6} />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                  <Tooltip formatter={(v) => `${Math.round(v * 1000) / 10}%`} contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                  <Bar dataKey="mention_rate" fill={CHART_COLORS[0]} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Sentiment trend" subtitle="Average sentiment across scans" className="lg:col-span-6" testId="chart-sentiment-card">
          {noData || timeline.length === 0 ? (
            <EmptyState title="No data yet" description="Sentiment appears after a scan completes." />
          ) : (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSIONS}>
                <LineChart data={timeline} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.6} />
                  <XAxis dataKey="x" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} domain={[-1, 1]} />
                  <Tooltip contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                  <Line type="monotone" dataKey="avg_sentiment" stroke={CHART_COLORS[2]} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
